import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, ExternalLink, CreditCard, ChevronRight, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

const formatCents = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-slate-500',
  open: 'bg-blue-500',
  sent: 'bg-indigo-500',
  paid: 'bg-emerald-500',
  partially_paid: 'bg-amber-500',
  void: 'bg-red-500',
  uncollectible: 'bg-red-700',
};

interface Invoice {
  id: string;
  invoiceNumber: string;
  status: string;
  issuedAt: string;
  dueAt: string | null;
  totalCents: number;
  amountPaidCents: number;
  balanceCents: number;
  paymentUrl: string | null;
  lineItems: Array<{
    id: string;
    description: string;
    quantity: number;
    unitPriceCents: number;
    amountCents: number;
    cptCode: string | null;
  }>;
}

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

interface PatientInvoicesPortalProps {
  sessionToken: string;
  clinicName: string;
  patientName?: string;
}

export default function PatientInvoicesPortal({
  sessionToken,
  clinicName,
  patientName,
}: PatientInvoicesPortalProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [removingCard, setRemovingCard] = useState<string | null>(null);

  const apiHeaders = { 'x-session-token': sessionToken };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [invoicesRes, cardsRes] = await Promise.all([
          axios.get(`${API_BASE}/patient-portal/my-invoices`, { headers: apiHeaders }),
          axios.get(`${API_BASE}/patient-portal/payment-methods`, { headers: apiHeaders }),
        ]);
        setInvoices(invoicesRes.data?.data || []);
        setPaymentMethods(cardsRes.data?.data || []);
      } catch (err) {
        console.error('Failed to load portal data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [sessionToken]);

  const handleRemoveCard = async (pmId: string) => {
    setRemovingCard(pmId);
    try {
      await axios.delete(`${API_BASE}/patient-portal/payment-methods/${pmId}`, { headers: apiHeaders });
      setPaymentMethods((prev) => prev.filter((pm) => pm.id !== pmId));
    } catch (err) {
      console.error('Failed to remove card:', err);
    } finally {
      setRemovingCard(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Invoice detail view
  if (selectedInvoice) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <div className="w-full max-w-lg space-y-4">
          <div className="text-center">
            <img src="/novamed_logo_white.svg" alt="NovaMed" className="mx-auto h-8 mb-2" />
            <p className="text-slate-400 text-sm">{clinicName}</p>
          </div>

          <Card className="border-slate-700 bg-slate-800 text-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Invoice #{selectedInvoice.invoiceNumber}</CardTitle>
                  <CardDescription className="text-slate-400">
                    {selectedInvoice.issuedAt
                      ? `Issued ${new Date(selectedInvoice.issuedAt).toLocaleDateString()}`
                      : 'Draft'}
                    {selectedInvoice.dueAt && ` | Due ${new Date(selectedInvoice.dueAt).toLocaleDateString()}`}
                  </CardDescription>
                </div>
                <Badge className={`${STATUS_STYLES[selectedInvoice.status] || 'bg-slate-500'} text-white border-0`}>
                  {selectedInvoice.status.replace('_', ' ')}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Line items */}
              <div className="space-y-2">
                {selectedInvoice.lineItems.map((li) => (
                  <div key={li.id} className="flex justify-between text-sm py-1 border-b border-slate-700">
                    <div>
                      <p>{li.description}</p>
                      {li.cptCode && <p className="text-xs text-slate-500">CPT: {li.cptCode}</p>}
                    </div>
                    <p className="font-medium">{formatCents(li.amountCents)}</p>
                  </div>
                ))}
              </div>

              <div className="flex justify-between font-bold text-lg border-t border-slate-600 pt-3">
                <span>Balance Due</span>
                <span>{formatCents(selectedInvoice.balanceCents)}</span>
              </div>

              {selectedInvoice.paymentUrl && selectedInvoice.balanceCents > 0 && (
                <a
                  href={selectedInvoice.paymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-brand-teal hover:bg-brand-teal/80">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Pay Now {formatCents(selectedInvoice.balanceCents)}
                    <ExternalLink className="ml-2 h-3 w-3" />
                  </Button>
                </a>
              )}

              <Button
                variant="ghost"
                className="w-full text-slate-400 hover:text-white"
                onClick={() => setSelectedInvoice(null)}
              >
                Back to Invoices
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main list view
  return (
    <div className="flex min-h-screen justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 pt-8">
      <div className="w-full max-w-lg space-y-4">
        <div className="text-center">
          <img src="/novamed_logo_white.svg" alt="NovaMed" className="mx-auto h-8 mb-2" />
          <p className="text-slate-400 text-sm">{clinicName}</p>
          {patientName && <p className="text-white font-medium mt-1">{patientName}</p>}
        </div>

        {/* Invoices */}
        <Card className="border-slate-700 bg-slate-800 text-white">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-brand-teal" />
              <CardTitle className="text-lg">My Invoices</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No invoices found.</p>
            ) : (
              <div className="space-y-2">
                {invoices.map((inv) => (
                  <button
                    key={inv.id}
                    className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-brand-teal/50 transition-colors text-left"
                    onClick={() => setSelectedInvoice(inv)}
                  >
                    <div>
                      <p className="font-medium text-sm">#{inv.invoiceNumber}</p>
                      <p className="text-xs text-slate-400">
                        {inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString() : 'Draft'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="font-semibold text-sm">{formatCents(inv.totalCents)}</p>
                        <Badge
                          className={`${STATUS_STYLES[inv.status] || 'bg-slate-500'} text-white border-0 text-xs`}
                        >
                          {inv.status === 'paid' ? 'Paid' : inv.balanceCents > 0 ? `${formatCents(inv.balanceCents)} due` : inv.status}
                        </Badge>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-500" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Saved Payment Methods */}
        <Card className="border-slate-700 bg-slate-800 text-white">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-brand-teal" />
              <CardTitle className="text-lg">Saved Cards</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {paymentMethods.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No saved payment methods.</p>
            ) : (
              <div className="space-y-2">
                {paymentMethods.map((pm) => (
                  <div
                    key={pm.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-12 rounded bg-slate-700 flex items-center justify-center text-xs font-bold uppercase">
                        {pm.brand}
                      </div>
                      <div>
                        <p className="font-medium text-sm">**** {pm.last4}</p>
                        <p className="text-xs text-slate-400">
                          Expires {pm.expMonth}/{pm.expYear}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:text-red-400"
                      disabled={removingCard === pm.id}
                      onClick={() => handleRemoveCard(pm.id)}
                    >
                      {removingCard === pm.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
